import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Download, Loader2, RefreshCw, Users, CheckCircle2, Moon, XCircle, MailX, Trash2, Megaphone } from "lucide-react";

// Common distribution-list aliases that will never register opens because they're
// relay addresses, not mailboxes. We flag these in the UI so you don't try to
// "sunset" them.
const ALIAS_LOCALPARTS = new Set([
  "all", "allstaff", "staff", "team", "info", "contact", "hello", "hi",
  "support", "admin", "office", "it", "hr", "accounts", "finance",
  "communications", "comms", "newsletter", "noreply", "no-reply",
]);
function isDistributionAlias(email) {
  const local = (email || "").split("@")[0]?.toLowerCase() || "";
  return ALIAS_LOCALPARTS.has(local) || local.startsWith("all") || local.endsWith("-team") || local.endsWith("-list");
}

const TIER_META = {
  active:         { label: "Active",         icon: CheckCircle2, color: "#0F7A3D", bg: "#E6F4EB", desc: "Opened at least one mailer" },
  dormant:        { label: "Dormant",        icon: Moon,         color: "#B4530A", bg: "#FBEBD8", desc: "Received but never opened" },
  not_delivering: { label: "Not delivering", icon: XCircle,      color: "#B4271A", bg: "#FCE1DC", desc: "Bounced or never got through" },
  unsubscribed:   { label: "Unsubscribed",   icon: MailX,        color: "#57534E", bg: "#EFECE7", desc: "Opted out" },
};

const TIER_ORDER = ["not_delivering", "dormant", "active", "unsubscribed"];

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 10);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

async function downloadTierCsv(tier) {
  const params = {};
  if (tier) params.tier = tier;
  const res = await api.get("/reports/staff-engagement.csv", { params, responseType: "blob" });
  const filename = tier ? `staff_engagement_${tier}.csv` : "staff_engagement.csv";
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function StaffEngagement() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [q, setQ] = useState("");
  const [downloadingTier, setDownloadingTier] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/reports/staff-engagement");
      setData(data);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totals = data?.totals || { active: 0, dormant: 0, not_delivering: 0, unsubscribed: 0, total: 0 };
  const rows = data?.rows || [];

  const filteredRows = useMemo(() => {
    let out = rows;
    if (tierFilter !== "all") out = out.filter((r) => r.tier === tierFilter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((r) => (r.email || "").toLowerCase().includes(needle) || (r.name || "").toLowerCase().includes(needle));
    }
    return out;
  }, [rows, tierFilter, q]);

  const runDownload = async (tier) => {
    setDownloadingTier(tier || "all");
    try {
      await downloadTierCsv(tier);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail || e.message));
    } finally {
      setDownloadingTier("");
    }
  };

  const confirmDelete = (row) => setDeleteTarget(row);

  const runDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    setError("");
    try {
      await api.delete(`/recipients/${deleteTarget.id}`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail || e.message));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="staff-engagement-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest mb-2" style={{ color: "var(--rahi-soft)" }}>
            <Users size={12} /> Staff only
          </div>
          <h1 className="font-display text-4xl font-black tracking-tight">
            Staff <span style={{ color: "var(--rahi-red)" }}>engagement</span>
          </h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--rahi-soft)" }}>
            Which internal IDs actually receive and read mailers. Helps decide which mailboxes to sunset, reassign, or nudge into use.
            <br />
            <span className="text-xs">Data is cumulative across every campaign sent through this app. Note: open tracking uses a pixel — some inboxes block images, so "dormant" is a soft signal, not proof.</span>
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="btn btn-ghost"
          data-testid="staff-refresh-btn"
        >
          {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-md border flex gap-3 items-start" style={{ borderColor: "#F3B4A5", background: "#FFF4F0" }} data-testid="staff-error">
          <XCircle size={16} className="mt-0.5" style={{ color: "var(--rahi-red)" }} />
          <div className="text-sm" style={{ color: "#8A2A16" }}>{error}</div>
        </div>
      )}

      {/* Tier cards (clickable filters + download) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {TIER_ORDER.map((t) => {
          const meta = TIER_META[t];
          const Icon = meta.icon;
          const count = totals[t] || 0;
          const pct = totals.total > 0 ? Math.round((count / totals.total) * 100) : 0;
          const isActive = tierFilter === t;
          return (
            <button
              key={t}
              onClick={() => setTierFilter(isActive ? "all" : t)}
              className="rounded-lg border p-5 text-left transition-all"
              style={{
                borderColor: isActive ? meta.color : "var(--rahi-border)",
                background: isActive ? meta.bg : "var(--rahi-surface)",
                boxShadow: isActive ? `0 0 0 1px ${meta.color}` : "none",
              }}
              data-testid={`staff-tier-card-${t}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold" style={{ color: meta.color }}>
                  <Icon size={14} /> {meta.label}
                </div>
                <span className="text-xs" style={{ color: "var(--rahi-soft)" }}>{pct}%</span>
              </div>
              <div className="font-display text-3xl font-black tabular-nums mb-1">{count.toLocaleString()}</div>
              <div className="text-xs" style={{ color: "var(--rahi-soft)" }}>{meta.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Filter + download row */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email or name…"
          className="flex-1 min-w-[240px] px-3 py-2 rounded-md border text-sm bg-white"
          style={{ borderColor: "var(--rahi-border)" }}
          data-testid="staff-search-input"
        />
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          className="px-3 py-2 rounded-md border text-sm bg-white"
          style={{ borderColor: "var(--rahi-border)" }}
          data-testid="staff-tier-filter"
        >
          <option value="all">All tiers</option>
          {TIER_ORDER.map((t) => (
            <option key={t} value={t}>{TIER_META[t].label}</option>
          ))}
        </select>
        <button
          onClick={() => runDownload(tierFilter === "all" ? null : tierFilter)}
          disabled={downloadingTier !== "" || totals.total === 0}
          className="btn btn-primary"
          data-testid="staff-download-btn"
          title={tierFilter === "all" ? "Download all staff rows as CSV" : `Download only ${TIER_META[tierFilter].label} rows`}
        >
          {downloadingTier ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
          {tierFilter === "all" ? "Download all staff CSV" : `Download ${TIER_META[tierFilter].label} CSV`}
        </button>
      </div>

      {/* Roster */}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--rahi-border)", background: "var(--rahi-surface)" }}>
        {loading ? (
          <div className="p-10 text-center text-sm flex items-center gap-2 justify-center" style={{ color: "var(--rahi-soft)" }}>
            <Loader2 className="animate-spin" size={14} /> Loading staff engagement…
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="p-10 text-center text-sm" style={{ color: "var(--rahi-soft)" }}>
            {totals.total === 0 ? "No recipients tagged `staff` yet." : "No rows match this filter."}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="staff-roster-table">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: "var(--rahi-border)", background: "#FBF9F6" }}>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--rahi-soft)" }}>Email</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--rahi-soft)" }}>Name</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--rahi-soft)" }}>Tier</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--rahi-soft)" }}>Last delivered</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--rahi-soft)" }}>Last opened</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--rahi-soft)" }}>Last clicked</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold text-right" style={{ color: "var(--rahi-soft)" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => {
                const meta = TIER_META[r.tier] || TIER_META.dormant;
                const Icon = meta.icon;
                const isAlias = isDistributionAlias(r.email);
                return (
                  <tr key={r.email} className="border-b hover:bg-[#FBF9F6]" style={{ borderColor: "var(--rahi-border)" }} data-testid={`staff-row-${r.email}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{r.email}</span>
                        {isAlias && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                            style={{ background: "#EEF2FF", color: "#4338CA" }}
                            title="Distribution alias — this is a relay address, not a mailbox. Opens cannot be tracked."
                            data-testid={`staff-alias-badge-${r.email}`}
                          >
                            <Megaphone size={10} /> alias
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3" style={{ color: "var(--rahi-soft)" }}>{r.name || "—"}</td>
                    <td className="px-5 py-3">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold"
                        style={{ background: meta.bg, color: meta.color }}
                      >
                        <Icon size={11} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs tabular-nums" style={{ color: "var(--rahi-soft)" }}>{formatDate(r.last_delivered_at)}</td>
                    <td className="px-5 py-3 text-xs tabular-nums" style={{ color: "var(--rahi-soft)" }}>{formatDate(r.last_opened_at)}</td>
                    <td className="px-5 py-3 text-xs tabular-nums" style={{ color: "var(--rahi-soft)" }}>{formatDate(r.last_clicked_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => confirmDelete(r)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-[#FFF4F0]"
                        style={{ color: "var(--rahi-red)" }}
                        data-testid={`staff-delete-${r.email}`}
                        title="Delete this recipient permanently"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer summary */}
      {!loading && totals.total > 0 && (
        <div className="mt-4 text-xs text-right" style={{ color: "var(--rahi-soft)" }}>
          Showing {filteredRows.length.toLocaleString()} of {totals.total.toLocaleString()} staff recipients
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(28, 25, 23, 0.65)", backdropFilter: "blur(4px)" }}
          onClick={() => !deleting && setDeleteTarget(null)}
          data-testid="staff-delete-modal"
        >
          <div
            className="rounded-lg border max-w-md w-full p-6 shadow-xl"
            style={{ borderColor: "var(--rahi-border)", background: "var(--rahi-surface)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <Trash2 size={16} style={{ color: "var(--rahi-red)" }} />
              <h3 className="font-display text-lg font-bold">Delete this recipient?</h3>
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--rahi-soft)" }}>
              This permanently removes <strong style={{ color: "var(--rahi-ink)" }}>{deleteTarget.email}</strong> from your recipient list across every group they're tagged in. This cannot be undone.
            </p>
            {isDistributionAlias(deleteTarget.email) && (
              <div className="mb-4 p-3 rounded border text-xs" style={{ borderColor: "#C7D2FE", background: "#EEF2FF", color: "#4338CA" }}>
                <strong>Heads up:</strong> this looks like a distribution alias. Deleting it removes the relay address from your mailer, but the underlying mailing list on the mail server stays intact.
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="btn btn-ghost"
                data-testid="staff-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={runDelete}
                disabled={deleting}
                className="btn btn-primary"
                style={{ background: "var(--rahi-red)" }}
                data-testid="staff-delete-confirm"
              >
                {deleting ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
